import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from 'components/page-header';
import moment from 'moment';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  DataTableSkeleton,
  Dropdown,
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';
import { PriceCardDeck, type PriceCardProps } from 'components/price-card';
import { PriceCurvePreviewChart } from 'components/price-curve-chart/price-curve-preview-chart';
import { getVisibleSliceBounds } from 'components/price-curve-chart/types';
import { PriceScheduleTable } from './price-schedule-table';
import {
  sourcesAtom,
  sourcesLoadingAtom,
  closingDatesAtom,
  closingDatesLoadingAtom,
  closingDatesHasMoreAtom,
  selectedSourceAtom,
  selectedDateAtom,
  fetchSourcesAtom,
  fetchClosingDatesAtom,
  fetchMoreClosingDatesAtom,
  stripPriceDataAtom,
  stripPriceLoadingAtom,
  fetchStripPriceDataAtom,
} from './atoms';
import type { SourceEntry } from './types';
import style from './forward-price-curve-content.module.scss';

export function ForwardPriceCurveContent() {
  const sources = useAtomValue(sourcesAtom);
  const sourcesLoading = useAtomValue(sourcesLoadingAtom);
  const closingDates = useAtomValue(closingDatesAtom);
  const closingDatesLoading = useAtomValue(closingDatesLoadingAtom);
  const closingDatesHasMore = useAtomValue(closingDatesHasMoreAtom);
  const [selectedSource, setSelectedSource] = useAtom(selectedSourceAtom);
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom);
  const stripPriceData = useAtomValue(stripPriceDataAtom);
  const stripPriceLoading = useAtomValue(stripPriceLoadingAtom);
  const fetchSources = useSetAtom(fetchSourcesAtom);
  const fetchClosingDates = useSetAtom(fetchClosingDatesAtom);
  const fetchMoreClosingDates = useSetAtom(fetchMoreClosingDatesAtom);
  const fetchStripPriceData = useSetAtom(fetchStripPriceDataAtom);

  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const [visibleXMin, setVisibleXMin] = useState<number | undefined>();
  const [visibleXMax, setVisibleXMax] = useState<number | undefined>();

  const handleVisibleRangeChange = useCallback(
    (xMin: number | undefined, xMax: number | undefined) => {
      setVisibleXMin(xMin);
      setVisibleXMax(xMax);
    },
    []
  );

  // Attach scroll listener to the dropdown listbox for infinite scroll
  useEffect(() => {
    const wrapper = dateDropdownRef.current;
    if (!wrapper) return;

    const handleScroll = (e: Event) => {
      const menu = e.target as HTMLElement;
      const nearBottom =
        menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 50;
      if (nearBottom && closingDatesHasMore && !closingDatesLoading) {
        fetchMoreClosingDates();
      }
    };

    let listbox: Element | null = null;

    const observer = new MutationObserver(() => {
      const el = wrapper.querySelector('[role="listbox"]');
      if (el && el !== listbox) {
        listbox?.removeEventListener('scroll', handleScroll);
        listbox = el;
        listbox.addEventListener('scroll', handleScroll);
      }
    });

    observer.observe(wrapper, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      listbox?.removeEventListener('scroll', handleScroll);
    };
  }, [closingDatesHasMore, closingDatesLoading, fetchMoreClosingDates]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    if (selectedSource) {
      fetchClosingDates(selectedSource);
    }
  }, [selectedSource, fetchClosingDates]);

  useEffect(() => {
    if (selectedDate) {
      fetchStripPriceData(selectedDate);
    }
  }, [selectedDate, fetchStripPriceData]);

  const priceCards: PriceCardProps[] = useMemo(() => {
    const cp = stripPriceData?.currentPrices;
    if (!cp) return [];
    return [
      { label: 'Front Month Oil', value: cp.currentOil, unit: '$/BBL' },
      {
        label: '12-Mo Avg Oil',
        value: cp.avg12moOil,
        unit: '$/BBL forward avg',
      },
      {
        label: '60-Mo Avg Oil',
        value: cp.avg60moOil,
        unit: '$/BBL forward avg',
      },
      { label: 'Front Month Gas', value: cp.currentGas, unit: '$/MMBTU' },
      {
        label: '12-Mo Avg Gas',
        value: cp.avg12moGas,
        unit: '$/MMBTU forward avg',
      },
      {
        label: '60-Mo Avg Gas',
        value: cp.avg60moGas,
        unit: '$/MMBTU forward avg',
      },
    ];
  }, [stripPriceData]);

  const { chartSeries, chartDates, referenceLineTs } = useMemo(() => {
    if (!stripPriceData)
      return {
        chartSeries: null,
        chartDates: [] as number[],
        referenceLineTs: undefined,
      };
    const parsedDates = stripPriceData.pricingDates.map((d) =>
      moment.utc(d, 'YYYY-MM-DD').valueOf()
    );
    const closingTs = moment
      .utc(stripPriceData.closingDate, 'YYYY-MM-DD')
      .valueOf();

    const toPoints = (values: (number | string | null)[]) =>
      parsedDates
        .map(
          (ts, i) =>
            [ts, values[i] !== null ? Number(values[i]) : null] as [
              number,
              number | null,
            ]
        )
        .filter((p): p is [number, number] => p[1] !== null);

    return {
      chartSeries: {
        oil: toPoints(stripPriceData.oilPrices),
        gas: toPoints(stripPriceData.gasPrices),
        valOil: toPoints(stripPriceData.valuationOilPrices),
        valGas: toPoints(stripPriceData.valuationGasPrices),
      },
      chartDates: parsedDates,
      referenceLineTs: closingTs,
    };
  }, [stripPriceData]);

  const filteredStripData = useMemo(() => {
    if (!stripPriceData) return stripPriceData;

    const bounds = getVisibleSliceBounds(chartDates, visibleXMin, visibleXMax);
    if (!bounds) return stripPriceData;

    const { start, end } = bounds;
    return {
      ...stripPriceData,
      pricingDates: stripPriceData.pricingDates.slice(start, end),
      oilPrices: stripPriceData.oilPrices.slice(start, end),
      gasPrices: stripPriceData.gasPrices.slice(start, end),
      valuationOilPrices: stripPriceData.valuationOilPrices.slice(start, end),
      valuationGasPrices: stripPriceData.valuationGasPrices.slice(start, end),
    };
  }, [stripPriceData, chartDates, visibleXMin, visibleXMax]);

  const formatSource = (source: string) =>
    source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Forward Price Curve"
        actions={<></>}
      />
      <div className={style.page}>
        <section className={style.whitePanel}>
          <div className={style.contextHeader}>
            <div>
              <h3 className={style.sectionTitle}>
                Pricing Context &amp; Controls
              </h3>
              {selectedDate ? (
                <span className={style.ingestLabel}>
                  Ingested {selectedDate}
                </span>
              ) : null}
            </div>
            <div className={style.controls}>
              <Dropdown
                id="source-dropdown"
                titleText=""
                label=""
                size="sm"
                className={style.dropdown}
                items={sources}
                selectedItem={
                  sources.find((s) => s.source === selectedSource) ?? null
                }
                itemToString={(item: SourceEntry | null) => {
                  return item ? formatSource(item.source) : '';
                }}
                disabled={sourcesLoading}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: SourceEntry | null;
                }) => {
                  if (selectedItem) {
                    setSelectedSource(selectedItem.source);
                  }
                }}
              />
              <div ref={dateDropdownRef}>
                <Dropdown
                  id="date-dropdown"
                  titleText=""
                  label=""
                  size="sm"
                  className={style.dropdown}
                  items={closingDates}
                  selectedItem={selectedDate}
                  disabled={closingDatesLoading}
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: string | null;
                  }) => {
                    if (selectedItem) {
                      setSelectedDate(selectedItem);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {stripPriceLoading ? (
            <div className={style.skeletonCards}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonPlaceholder key={i} className={style.skeletonCard} />
              ))}
            </div>
          ) : priceCards.length > 0 ? (
            <PriceCardDeck items={priceCards} />
          ) : null}
        </section>

        <div className={style.content}>
          {stripPriceLoading ? (
            <>
              <section className={style.whitePanel}>
                <SkeletonText heading width="200px" />
                <SkeletonPlaceholder className={style.skeletonChart} />
              </section>
              <section className={style.whitePanel}>
                <SkeletonText heading width="150px" />
                <DataTableSkeleton
                  columnCount={5}
                  rowCount={5}
                  showHeader={false}
                  showToolbar={false}
                />
              </section>
            </>
          ) : (
            <>
              {chartSeries && (
                <section className={style.whitePanel}>
                  <h3 className={style.sectionTitle}>Price Curve Preview</h3>
                  <PriceCurvePreviewChart
                    series={chartSeries}
                    dates={chartDates}
                    referenceLineTs={referenceLineTs}
                    filterMode="range"
                    onVisibleRangeChange={handleVisibleRangeChange}
                  />
                </section>
              )}
              {filteredStripData && (
                <section className={style.whitePanel}>
                  <h3 className={style.sectionTitle}>Price Schedule</h3>
                  <PriceScheduleTable
                    key={filteredStripData.pricingDates.length}
                    data={filteredStripData}
                  />
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
